import { Download, FileDown, FileSpreadsheet, Plus, Search, Trash2, Upload } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button, ColBadge, Confirm, Empty, Field, Input, Modal, MoneyInput, PageHeader, Select, StatusBadge } from "../components/ui";
import { SyncCounter } from "../components/SyncCounter";
import { ImportDialog } from "../components/ImportDialog";
import { api, downloadFile, errMsg } from "../lib/api";
import { ADMIN_ROLES, WRITE_ROLES, useAuth } from "../lib/auth";
import { addMonthsStr, fmtDate, fmtIDR, todayStr, useGuarantors } from "../lib/format";

export default function Loans() {
  const { can, user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState("active");
  const [imp, setImp] = useState(false);
  const [search, setSearch] = useState("");
  const [col, setCol] = useState("0");
  const [guarantor, setGuarantor] = useState("");
  const guarantors = useGuarantors();
  const [form, setForm] = useState<any>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState<string[]>([]);
  const [bulkDel, setBulkDel] = useState(false);
  const allChecked = rows.length > 0 && rows.every((r) => sel.includes(r.id));
  const toggleAll = () => setSel(allChecked ? [] : rows.map((r) => r.id));
  const toggle = (id: string) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const removeBulk = async () => {
    try {
      const { data } = await api.post("/loans/bulk-delete", { ids: sel });
      toast.success(`${data.deleted} pinjaman dipindahkan ke tempat sampah${data.skipped.length ? `, ${data.skipped.length} dilewati (sudah ada pembayaran)` : ""}. Dashboard & matriks otomatis tersinkron.`, { duration: 8000 });
      if (data.skipped.length) toast.info(data.skipped.map((s: any) => `${s.internal_id} ${s.name}: ${s.reason}`).join(" · "), { duration: 10000 });
      setSel([]); setBulkDel(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const load = useCallback(() => {
    api.get("/loans", { params: { status, search, col, guarantor } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status, search, col, guarantor]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openForm = useCallback(async (studentId = "", pre: any = null) => {
    const r = await api.get("/students", { params: { status: "active" } });
    setStudents(r.data.filter((s: any) => !s.active_loan));
    const disb = pre?.disbursement_date || todayStr();
    setForm({ student_id: studentId, principal: pre?.principal || "", tenor: String(pre?.tenor || 12), installment: pre?.installment || "", disbursement_date: disb, start_date: pre?.start_date || addMonthsStr(disb, 2), guarantor: pre?.guarantor || "", note: pre?.note || "" });
  }, []);
  useEffect(() => {
    const sid = params.get("student");
    if (!sid) return;
    const raw = sessionStorage.getItem("manual_loan");
    sessionStorage.removeItem("manual_loan");
    openForm(sid, raw ? JSON.parse(raw) : null);
  }, [params, openForm]);

  const defaultInst = form?.principal && form?.tenor ? Math.ceil(Number(form.principal) / Number(form.tenor) / 1000) * 1000 : 0;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/loans", { ...form, principal: Number(form.principal), tenor: Number(form.tenor), installment: form.installment ? Number(form.installment) : null });
      toast.success("Pinjaman dibuat, jadwal cicilan terbentuk");
      setForm(null);
      nav(`/pinjaman/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="loans-page">
      <PageHeader title="Akun Pinjaman" subtitle={<span className="flex flex-wrap items-center gap-2">Pinjaman untuk siswa yang sudah terdaftar · siswa baru + pinjaman: gunakan tab Siswa <SyncCounter highlight="loans" refresh={rows} /></span>} actions={<div className="flex flex-wrap gap-2">
        {can(ADMIN_ROLES) && <>
          <Button variant="outline" data-testid="loan-import-template" onClick={() => downloadFile("/io/template", "template_yutaka_edu_finance.xlsx").catch((e) => toast.error(errMsg(e)))}><FileSpreadsheet className="h-4 w-4" /> Template</Button>
          <Button variant="outline" data-testid="loan-import-button" onClick={() => setImp(true)}><Upload className="h-4 w-4" /> Import Excel/CSV</Button>
          <Button variant="outline" data-testid="loan-export-xlsx" onClick={() => downloadFile("/io2/export/loans?format=xlsx", "pinjaman.xlsx").catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /> Excel</Button>
          <Button variant="outline" data-testid="loan-export-csv" onClick={() => downloadFile("/io2/export/loans?format=csv", "pinjaman.csv").catch((e) => toast.error(errMsg(e)))}><FileSpreadsheet className="h-4 w-4" /> CSV</Button>
          <Button variant="outline" data-testid="loan-export-pdf" onClick={() => downloadFile("/io/export/loans.pdf", "pinjaman.pdf", true).catch((e) => toast.error(errMsg(e)))}><FileDown className="h-4 w-4" /> PDF</Button>
        </>}
        {can(WRITE_ROLES) && <Button data-testid="loan-add-button" onClick={() => openForm()}><Plus className="h-4 w-4" /> Pinjaman untuk Siswa Terdaftar</Button>}
      </div>} />
      <div className="card-surface p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input data-testid="loan-search-input" className="pl-9" placeholder="Cari nama / NIK / ID internal…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select data-testid="loan-status-filter" className="sm:w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Semua</option>
          <option value="active">Aktif</option>
          <option value="lunas">Lunas</option>
        </Select>
        <Select data-testid="loan-col-filter" className="sm:w-44" value={col} onChange={(e) => setCol(e.target.value)}>
          <option value="0">Semua Col</option>
          {[1, 2, 3, 4, 5].map((c) => <option key={c} value={c}>Col {c}</option>)}
        </Select>
        <Select data-testid="loan-guarantor-filter" className="sm:w-72" value={guarantor} onChange={(e) => setGuarantor(e.target.value)}>
          <option value="">Semua penjamin</option>
          {guarantors.map((g) => <option key={g} value={g}>{g}</option>)}
        </Select>
      </div>
      {guarantor && <p className="text-xs text-muted-foreground mb-2" data-testid="loan-guarantor-summary">{rows.length} pinjaman · outstanding {fmtIDR(rows.reduce((a, r) => a + r.metrics.outstanding, 0))} · tunggakan {fmtIDR(rows.reduce((a, r) => a + r.metrics.overdue_amount, 0))}</p>}
      {isSuper && sel.length > 0 && (
        <div className="mb-2 flex items-center gap-3 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 px-3 py-2 text-sm" data-testid="loan-bulk-bar">
          <span className="font-semibold">{sel.length} pinjaman ditandai</span>
          <Button variant="danger" size="sm" onClick={() => setBulkDel(true)} data-testid="loan-bulk-delete-button"><Trash2 className="h-4 w-4" /> Hapus yang ditandai</Button>
          <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setSel([])} data-testid="loan-bulk-clear">Batal tandai</button>
        </div>
      )}
      <div className="card-surface overflow-x-auto">
        <table className="yt-table" data-testid="loans-table">
          <thead><tr>{isSuper && <th className="w-8"><input type="checkbox" data-testid="loan-select-all" aria-label="Tandai semua" checked={allChecked} onChange={toggleAll} /></th>}<th>Siswa</th><th>Pinjaman</th><th>Cicilan</th><th>Progress</th><th>Outstanding</th><th>Status Bayar</th><th>Jatuh Tempo Berikut</th><th>Col</th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="cursor-pointer" onClick={() => nav(`/pinjaman/${l.id}`)} data-testid={`loan-row-${l.student.internal_id}`}>
                {isSuper && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`loan-select-${l.student.internal_id}`} checked={sel.includes(l.id)} onChange={() => toggle(l.id)} /></td>}
                <td>
                  <p className="font-semibold">{l.student.name}</p>
                  <p className="text-xs text-muted-foreground num">{l.student.internal_id} · {l.student.unit}</p>
                </td>
                <td className="num">{fmtIDR(l.principal)}<p className="text-xs text-muted-foreground">{l.tenor} bln · cair {fmtDate(l.disbursement_date)} · cicilan mulai {fmtDate(l.start_date)}</p></td>
                <td className="num">{fmtIDR(l.installment)}</td>
                <td className="num text-xs">{l.metrics.paid_installments}/{l.metrics.total_installments}{l.schedule_version > 1 && <span className="ml-1 rounded bg-muted px-1">v{l.schedule_version}</span>}</td>
                <td className="num font-semibold">{fmtIDR(l.metrics.outstanding)}</td>
                <td><StatusBadge status={l.metrics.status} />{l.metrics.days_late > 0 && <p className="text-xs text-red-600 mt-1">{l.metrics.days_late} hari telat</p>}</td>
                <td className="num text-sm">{fmtDate(l.metrics.next_due)}</td>
                <td>{l.status === "active" ? <ColBadge col={l.metrics.col} /> : <span className="text-xs text-muted-foreground">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <Empty text="Tidak ada pinjaman sesuai filter." />}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title="Pinjaman Baru" testId="loan-form-modal">
        {form && (
          <form onSubmit={save} className="space-y-3">
            <Field label="Siswa (aktif, belum punya pinjaman aktif)">
              <Select data-testid="loan-student-select" required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}>
                <option value="">— pilih siswa —</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.internal_id} · {s.name} · {s.unit}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Total pinjaman (Rp)"><MoneyInput data-testid="loan-principal-input" terbilang required value={form.principal} onChange={(v) => setForm({ ...form, principal: v })} /></Field>
              <Field label="Tenor (bulan)"><Input data-testid="loan-tenor-input" type="number" min={1} max={120} required value={form.tenor} onChange={(e) => setForm({ ...form, tenor: e.target.value })} /></Field>
            </div>
            <Field label="Cicilan tetap (Rp)" hint={defaultInst ? `Kosongkan untuk otomatis: ${fmtIDR(defaultInst)} (dibulatkan ke ribuan, cicilan terakhir menyesuaikan)` : "Kosongkan untuk dihitung otomatis"}>
              <MoneyInput data-testid="loan-installment-input" value={form.installment} onChange={(v) => setForm({ ...form, installment: v })} placeholder={defaultInst ? String(defaultInst) : ""} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tgl pencairan / penerbangan"><Input data-testid="loan-disbursement-input" type="date" required value={form.disbursement_date} onChange={(e) => setForm({ ...form, disbursement_date: e.target.value, start_date: addMonthsStr(e.target.value, 2) })} /></Field>
              <Field label="Tgl mulai cicilan" hint="Otomatis +2 bulan dari pencairan · bisa diubah manual. Jatuh tempo pertama mengikuti tanggal seragam.">
                <Input data-testid="loan-start-input" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </Field>
            </div>
            <Field label="Penjamin pinjaman">
              <Select data-testid="loan-guarantor-input" required value={form.guarantor} onChange={(e) => setForm({ ...form, guarantor: e.target.value })}>
                <option value="">— pilih penjamin —</option>
                {guarantors.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            </Field>
            <Field label="Catatan"><Input data-testid="loan-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
              <Button type="submit" disabled={busy} data-testid="loan-save-button">{busy ? "Menyimpan…" : "Buat Pinjaman & Jadwal"}</Button>
            </div>
          </form>
        )}
      </Modal>
      <ImportDialog open={imp} onClose={() => setImp(false)} onDone={load} onManual={(p) => {
        if (p.kind === "loan") { openForm(p.student_id, p.loan); return; }
        sessionStorage.setItem("manual_student", JSON.stringify(p)); nav("/siswa?manual=1");
      }} />
      <Confirm open={bulkDel} onClose={() => setBulkDel(false)} onConfirm={removeBulk} danger title={`Hapus ${sel.length} pinjaman yang ditandai?`} text="Pinjaman beserta jadwal cicilannya dipindahkan ke Tempat Sampah (bisa dipulihkan dari Admin). Pinjaman yang sudah memiliki transaksi pembayaran akan dilewati. Dashboard, matriks, dan laporan otomatis menyesuaikan." testId="loan-bulk-delete-confirm" />
    </div>
  );
}
